import IconTextButton from '@/common/atoms/IconTextButton'
import { shuffleArray } from '@utils/shuffleArray'
import Loading from '@/common/atoms/Loading'
import TextErrors from '@/common/atoms/TextErrors'
import { Box, HStack, Tag, Text, useToast } from '@chakra-ui/react'
import { Droppable } from '@hello-pangea/dnd'
import {
  MeetingStepGovernanceThreadsFragment,
  MeetingStepThreadsFragment,
} from '@rolebase/shared/model/meeting_step'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { RandomIcon } from 'src/icons'
import ThreadSearchButton from '../../search/components/ThreadSearchButton'
import MeetingThreadQuickForm from './MeetingThreadQuickForm'
import { MeetingContext } from '../contexts/MeetingContext'
import { useMeetingNextSteps } from '../contexts/MeetingNextStepsContext'
import {
  Meeting_Step_Type_Enum,
  NextStepFragment,
  Thread_Status_Enum,
  useCircleNextStepsSubscription,
  useThreadsProposalsSubscription,
  useUpdateThreadMutation,
} from '@gql'
import { useOrgContext } from '@/org/contexts/OrgContext'
import MeetingStepContentThreadItem from './MeetingStepContentThreadItem'
import {
  filterThreadsForStep,
  findMoveTargetStep,
  isThreadsStep,
  threadTypeForStep,
} from './meetingStepThreadTypes'
import { groupApprovedProposals } from '@rolebase/shared/model/meetingProposals'
import {
  MeetingThreadsContext,
  ThreadMaybeAllowed,
} from './MeetingThreadsDragDropContext'
import {
  groupMeetingThreads,
  orderMembers,
} from '@rolebase/shared/model/meetingThreadsSort'
import {
  MeetingThreadsSortMode,
  defaultMeetingThreadsSortMode,
  defaultThreadWeather,
} from '@rolebase/shared/model/threadWeather'
import MeetingThreadsSortBar from './MeetingThreadsSortBar'
import MeetingThreadsLegend from './MeetingThreadsLegend'
import useInheritedThreadsSort from '../hooks/useInheritedThreadsSort'
import MeetingThreadsMembersBar from './MeetingThreadsMembersBar'

interface Props {
  step: MeetingStepThreadsFragment | MeetingStepGovernanceThreadsFragment
}

// Props of drop zone when empty
const dropzoneProps = {
  position: 'absolute',
  h: '100px',
  top: '-40px',
  right: 0,
  left: 0,
  zIndex: -1,
} as const

export default function MeetingStepContentThreads({ step }: Props) {
  const { t } = useTranslation()
  const toast = useToast()
  const { circle, editable, meeting, participants, steps } =
    useContext(MeetingContext)!
  const {
    threadsByStep,
    availableThreads,
    loading,
    error,
    add,
    remove,
    randomize,
    setSortMode,
    setMembersOrder,
    setThreadWeather,
    reportMembersOrder,
    reportThreadsOrder,
    getStepData,
  } = useContext(MeetingThreadsContext)!

  // What was just written shows at once, without waiting for the server's
  // echo (#106): every read of the step's settings goes through here.
  const stepData = getStepData(step)

  const selectedThreads = threadsByStep[step.id]
  const isEmpty = !selectedThreads || selectedThreads.length === 0

  // The next steps of the tensions on the agenda, shown under each of them
  // (G2-D25). Subscribed ONCE here and indexed by tension rather than once per
  // row: the same query repeated twenty times would open twenty subscriptions
  // for a single answer.
  const { orgData } = useOrgContext()
  const circlesIds = useMemo(
    () =>
      circle
        ? (orgData?.circleWithChildren(circle.id) ?? []).map((c) => c.id)
        : [],
    [orgData, circle]
  )
  // Shared with the Next steps board through the meeting (#42); subscribed
  // here only outside a meeting
  const meetingNextSteps = useMeetingNextSteps()
  const { data: ownNextStepsData } = useCircleNextStepsSubscription({
    skip: !!meetingNextSteps || circlesIds.length === 0,
    variables: { circlesIds },
  })
  const nextStepsList = meetingNextSteps
    ? meetingNextSteps.nextSteps
    : ownNextStepsData?.next_step

  // Neither archived nor done (G2-D26): under a tension one reads what is still
  // owed, not what has already been settled.
  const nextStepsByThread = useMemo(() => {
    const map = new Map<string, NextStepFragment[]>()
    for (const nextStep of nextStepsList ?? []) {
      if (nextStep.done || !nextStep.threadId) continue
      const list = map.get(nextStep.threadId)
      if (list) list.push(nextStep)
      else map.set(nextStep.threadId, [nextStep])
    }
    return map
  }, [nextStepsList])

  // What each tension produced, once its proposal was approved (REU-13).
  //
  // 🔑 Subscribed ONCE for the step and indexed by tension, exactly like the
  // next steps above — one subscription per row would repeat the same query as
  // many times as there are tensions on the agenda (REU13-D4, the waste
  // ENR-08 recorded).
  //
  // Only a Governance step asks for it (REU13-D1): elsewhere the query would
  // run for nothing, since nothing would be drawn with the answer.
  const threadsIds = useMemo(
    () => (selectedThreads ?? []).map((thread) => thread.id),
    [selectedThreads]
  )
  const isGovernanceStep =
    step.type === Meeting_Step_Type_Enum.ThreadsGovernance
  const { data: proposalsData } = useThreadsProposalsSubscription({
    skip: !isGovernanceStep || threadsIds.length === 0,
    variables: { threadsIds },
  })

  const proposalsByThread = useMemo(
    () => groupApprovedProposals(proposalsData?.thread_activity ?? []),
    [proposalsData]
  )

  // Closed on adding, free by hand (D4): the picker only offers threads of this
  // step's own type, while dragging between steps stays free and nothing is
  // ever ejected. The pool itself remains shared, so a thread already placed in
  // one step never shows up in the other.
  const addableThreads = useMemo(
    () => filterThreadsForStep(availableThreads, step.type),
    [availableThreads, step.type]
  )

  // The gesture names the type, so the modal opens with it already chosen
  // (D3 rule applied, not widened).

  // The thread steps of this meeting, in order, with the title the user sees.
  // A step's title lives in the meeting's stepsConfig, not on the step itself.
  // Computed here rather than in the item so the item keeps no context of its
  // own — MeetingThreadsDragDropContext already depends on it for a type.
  const threadSteps = useMemo(() => {
    if (!steps || !meeting) return []
    return steps
      .filter(
        (s) =>
          isThreadsStep(s.type) &&
          meeting.stepsConfig.some((sc) => sc.id === s.stepConfigId)
      )
      .map((s) => ({
        id: s.id,
        type: s.type,
        title:
          meeting.stepsConfig.find((sc) => sc.id === s.stepConfigId)?.title ??
          '',
      }))
  }, [steps, meeting])

  // Read back from the last meeting of this circle, and only while this step
  // has settled nothing of its own (REU89-D8).
  const inherited = useInheritedThreadsSort(
    step.type,
    stepData.sortMode === undefined || stepData.membersOrder === undefined
  )

  // The reading order of this step, shared by everyone (REU89-D7). Absent
  // everywhere means free order: that is how a circle without a past opens.
  const sortMode =
    stepData.sortMode ?? inherited.sortMode ?? defaultMeetingThreadsSortMode

  // The round: inherited order first, newcomers at the end (REU89-D11).
  const membersOrder = useMemo(
    () =>
      orderMembers(
        participants.map((p) => p.member.id),
        stepData.membersOrder ?? inherited.membersOrder
      ),
    [participants, stepData.membersOrder, inherited.membersOrder]
  )

  // The meeting's circle FIRST, then its roles (REU89-D10).
  const circlesOrder = useMemo(
    () =>
      circle
        ? [circle.id, ...circlesIds.filter((id) => id !== circle.id)]
        : circlesIds,
    [circle, circlesIds]
  )

  // Tensions reopened from the rainbow whose echo has not come back yet (#106).
  //
  // 🔑 The list only learns a status from the subscription, 1 to 2 s later
  // (the same wait as the checklist, BUG-02). Held here, not in the line, so
  // that the rainbow AND the group follow the click at once. The status icon
  // inside ThreadItem keeps its own copy and still waits for the echo.
  const [updateThread] = useUpdateThreadMutation()
  const [reopenedIds, setReopenedIds] = useState<string[]>([])

  // The server wins: an id is forgotten as soon as the list no longer reads
  // the tension as closed — reopened, or taken off the agenda.
  useEffect(() => {
    setReopenedIds((ids) => {
      const next = ids.filter((id) =>
        selectedThreads?.some(
          (thread) =>
            thread.id === id &&
            !('notAllowed' in thread) &&
            thread.status === Thread_Status_Enum.Closed
        )
      )
      return next.length === ids.length ? ids : next
    })
  }, [selectedThreads])

  // Active, like a tension born in a meeting (MeetingThreadQuickForm).
  const handleReopen = useCallback(
    async (threadId: string) => {
      setReopenedIds((ids) => [...ids, threadId])
      const rollback = () =>
        setReopenedIds((ids) => ids.filter((id) => id !== threadId))
      try {
        const { data } = await updateThread({
          variables: {
            id: threadId,
            values: { status: Thread_Status_Enum.Active },
          },
        })
        // A permission refusal is silent: null, not an error.
        if (!data?.update_thread_by_pk) rollback()
      } catch {
        rollback()
      }
    },
    [updateThread]
  )

  // One-click close from the line (#108). No optimistic state: the list reads
  // the status from its subscription, whose echo is immediate, and a refusal
  // must be said rather than shown then silently undone.
  const handleClose = useCallback(
    async (threadId: string) => {
      // A reopening still waiting for its echo must not mask this close
      setReopenedIds((ids) => ids.filter((id) => id !== threadId))
      try {
        const { data } = await updateThread({
          variables: {
            id: threadId,
            values: { status: Thread_Status_Enum.Closed },
          },
        })
        // A permission refusal is silent: null, not an error.
        if (!data?.update_thread_by_pk) throw new Error('refused')
      } catch {
        toast({ title: t('common.error'), status: 'error', duration: 3000 })
      }
    },
    [updateThread, toast, t]
  )

  // A tension one cannot read never claims to be settled: its status is not
  // readable either, so it stays in the list rather than sinking to the bottom
  // (REU89-D14 applies to what we can see).
  const isClosed = useCallback(
    (thread: ThreadMaybeAllowed) =>
      !('notAllowed' in thread) &&
      thread.status === Thread_Status_Enum.Closed &&
      !reopenedIds.includes(thread.id),
    [reopenedIds]
  )

  const grouping = useMemo(
    () =>
      groupMeetingThreads({
        threads: selectedThreads ?? [],
        mode: sortMode,
        weather: stepData.threadsWeather ?? {},
        circlesOrder,
        membersOrder,
        idOf: (thread) => thread.id,
        circleIdOf: (thread) =>
          'notAllowed' in thread ? undefined : thread.circleId,
        memberIdOf: (thread) =>
          'notAllowed' in thread ? undefined : thread.initiatorMemberId,
        isClosed,
      }),
    [
      selectedThreads,
      sortMode,
      stepData.threadsWeather,
      circlesOrder,
      membersOrder,
      isClosed,
    ]
  )

  // Tell the drag context what the list shows, settled tensions at the bottom
  // included: a drop reads its indexes off the screen, and the agenda no longer
  // has the same order.
  const displayedOrder = useMemo(
    () =>
      [
        ...grouping.flat,
        ...grouping.groups.flatMap((group) => group.threads),
        ...grouping.closed,
      ].map((thread) => thread.id),
    [grouping]
  )
  useEffect(() => {
    reportThreadsOrder(step.id, displayedOrder)
  }, [step.id, displayedOrder, reportThreadsOrder])

  // The participants in the round's order, for the bar.
  const orderedParticipants = useMemo(
    () =>
      membersOrder
        .map((id) => participants.find((p) => p.member.id === id))
        .filter((p): p is (typeof participants)[number] => p !== undefined),
    [membersOrder, participants]
  )

  const canShuffle =
    sortMode === MeetingThreadsSortMode.Member
      ? membersOrder.length > 2
      : sortMode === MeetingThreadsSortMode.Free &&
        (selectedThreads?.length ?? 0) > 2

  if (!circle) return null

  // The name of a group, resolved here: the sort itself only ever handles keys.
  const groupLabel = (key: string) => {
    if (sortMode === MeetingThreadsSortMode.Role) {
      const groupCircle = orgData?.getCircle(key)
      return orgData?.getRole(groupCircle?.roleId)?.name ?? ''
    }
    return orgData?.getMember(key)?.name ?? ''
  }

  return (
    <Box mt={5} mb={5}>
      <TextErrors errors={[error]} />

      {editable && (
        <>
          {/* The agenda is built here, at the top: the line is visible the
              moment one lands on the step (REU02-A1, A2). It replaces the
              "New tension" button and its modal — two ways of creating the
              same thing, one of them slower, do not justify each other.
              ⚠️ Deliberately outside the REUN-02 picker filter (circlesIds):
              a member often delegates writing their tension down to the
              meeting's secretary, who must be able to file it on any role of
              the meeting's circle, including one they don't participate in.
              Do not "fix" this by symmetry with the import picker below. */}
          <MeetingThreadQuickForm
            circleId={circle.id}
            stepType={step.type}
            onCreated={(id, weather) => {
              add(step.id, id)
              // Two distinct first-level keys, so `_append` never lets one
              // overwrite the other (REU89-D3). Nothing is written for the
              // default, exactly as for a tension nobody classified (D5).
              if (weather !== defaultThreadWeather) {
                setThreadWeather(step.id, id, weather)
              }
            }}
          />

          <HStack justifyContent="space-between" mb={4} flexWrap="wrap">
            <HStack spacing={1}>
              <MeetingThreadsSortBar
                value={sortMode}
                onChange={(mode) => setSortMode(step.id, mode)}
              />
              <MeetingThreadsLegend />
            </HStack>
            <HStack justifyContent="end">
              {loading && <Loading active size="sm" />}

              {/* Shuffling acts on what one is looking at: the tensions in the
                free order, the round in the by-member view, nothing in the two
                computed ones (REU89-D15). The drawn order only ever holds for
                today — it never touches the order the next meeting inherits. */}
              {canShuffle && (
                <IconTextButton
                  size="sm"
                  variant="outline"
                  aria-label={t(
                    sortMode === MeetingThreadsSortMode.Member
                      ? 'MeetingStepContentThreads.randomizeMembers'
                      : 'MeetingStepContentThreads.randomize'
                  )}
                  icon={<RandomIcon size={20} />}
                  onClick={() =>
                    sortMode === MeetingThreadsSortMode.Member
                      ? setMembersOrder(step.id, shuffleArray(membersOrder))
                      : randomize(step.id)
                  }
                />
              )}

              <ThreadSearchButton
                threads={addableThreads}
                createCircleId={circle.id}
                size="sm"
                variant="outline"
                rightIcon={
                  <Tag
                    colorScheme={addableThreads.length === 0 ? 'gray' : 'red'}
                    size="sm"
                  >
                    {addableThreads.length}
                  </Tag>
                }
                onSelect={(id) => add(step.id, id)}
              >
                {t('MeetingStepContentThreads.add')}
              </ThreadSearchButton>
            </HStack>
          </HStack>

          {sortMode === MeetingThreadsSortMode.Member && (
            <MeetingThreadsMembersBar
              stepId={step.id}
              members={orderedParticipants}
              onReport={reportMembersOrder}
            />
          )}
        </>
      )}

      <Box position="relative">
        <Droppable droppableId={step.id} type="meeting-threads">
          {(provided, snapshot) => {
            const fixZone = isEmpty && !snapshot.isDraggingOver
            return (
              <Box
                ref={provided.innerRef}
                {...(fixZone ? dropzoneProps : {})}
                {...provided.droppableProps}
              >
                {(() => {
                  // One running index for the whole zone: @hello-pangea/dnd
                  // needs the draggables of a droppable to be numbered without
                  // a hole, whatever the group headings between them.
                  let index = -1
                  const renderThread = (item: ThreadMaybeAllowed) => {
                    index++
                    // Where this thread could go if it sits in the wrong step.
                    // Undefined means silence: either it is well placed, or no
                    // step of its type exists here.
                    const target =
                      editable && !('notAllowed' in item)
                        ? findMoveTargetStep(item.type, step.type, threadSteps)
                        : undefined
                    return (
                      <MeetingStepContentThreadItem
                        key={item.id}
                        thread={item}
                        index={index}
                        stepId={step.id}
                        weather={stepData.threadsWeather?.[item.id]}
                        // No weather in the free order, and the rainbow follows
                        // the same rule (REU89-D9, D14).
                        showWeather={sortMode !== MeetingThreadsSortMode.Free}
                        closed={isClosed(item)}
                        onReopen={() => handleReopen(item.id)}
                        onClose={
                          editable ? () => handleClose(item.id) : undefined
                        }
                        // Only the free order writes the agenda: the three
                        // other modes are lenses, so nothing drags there.
                        dragDisabled={sortMode !== MeetingThreadsSortMode.Free}
                        nextSteps={nextStepsByThread.get(item.id)}
                        approvedProposals={proposalsByThread.get(item.id)}
                        onRemove={
                          editable ? () => remove(step.id, item.id) : undefined
                        }
                        moveTarget={
                          target
                            ? {
                                title: target.title,
                                onMove: () => {
                                  remove(step.id, item.id)
                                  add(target.id, item.id)
                                },
                              }
                            : undefined
                        }
                      />
                    )
                  }

                  return (
                    <>
                      {grouping.flat.map(renderThread)}

                      {grouping.groups.map((group) => (
                        <React.Fragment key={group.key}>
                          <Text
                            fontSize="sm"
                            fontWeight="bold"
                            color="gray.500"
                            mb={2}
                          >
                            {groupLabel(group.key)}
                            {group.empty && (
                              <Text as="span" fontWeight="normal" ml={2}>
                                {t('MeetingStepContentThreads.noThread')}
                              </Text>
                            )}
                          </Text>
                          {group.threads.map(renderThread)}
                        </React.Fragment>
                      ))}

                      {grouping.closed.length !== 0 && (
                        <Text
                          fontSize="sm"
                          fontWeight="bold"
                          color="gray.500"
                          mb={2}
                        >
                          {t('MeetingStepContentThreads.settled')}
                        </Text>
                      )}
                      {grouping.closed.map(renderThread)}
                    </>
                  )
                })()}
                {provided.placeholder}
              </Box>
            )
          }}
        </Droppable>
      </Box>
    </Box>
  )
}
